/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { DefaultButton, IButton } from '@fluentui/react/lib/Button';
import * as React from 'react';
import { useLocalization } from 'src/core/hooks/useLocalization';
import { getStylist } from 'src/core/themes/luisStylist';
import { BracesHandlerProps } from 'src/luis/verticals/patternInput/components/bracesHandlerProps';
import { getPatternEntityDropdownItems } from 'src/luis/verticals/patternInput/utils/patternEntityDropdown';

const { getClassName, styleDiv } = getStylist('PatternsPage');

const menuBtn = getClassName('MenuBtnClass', { width: 0, height: 0, visibility: 'hidden' });
const contextualMenuContainer = getClassName('contextualMenuContainer', { maxHeight: 300 });
const ContextualMenuContainer = styleDiv<{ yMenuPosition: number }>(
    'ContextualMenuContainer',
    null,
    ({ customProps: { yMenuPosition } }) => ({ position: 'absolute', top: yMenuPosition, left: 0 })
);

/**
 * Handles the representation of the contextual menu that
 * appears whenever the cursor is between a `{}`.
 */
export const BracesHandler = (props: BracesHandlerProps) => {
    const {
        entities,
        dropdownType: dropdown,
        parentRef,
        position,
        queryText,
        onCreateNewEntity,
        onCreateNewRole,
        focus,
        onItemClicked,
        onCreateNewEntityAndRole
    } = props;
    const { localize } = useLocalization();
    const componentRef = React.useRef<IButton>(null);

    React.useEffect(() => (dropdown !== 'none' ? componentRef.current.openMenu(false, false) : undefined), [dropdown, queryText]);
    React.useEffect(() => (focus ? componentRef.current.openMenu(false, true) : undefined), [focus]);

    const charWidth = 7.75;
    const inputFieldPosition = (parentRef ? parentRef.offsetLeft : 0) + 12;
    const xMenuPosition = inputFieldPosition + position * charWidth;
    const yMenuPosition = parentRef ? parentRef.offsetTop + parentRef.offsetHeight - 2 : 0;

    const items = getPatternEntityDropdownItems(
        dropdown,
        entities,
        queryText,
        {
            onItemClicked,
            onCreateNewEntity,
            onCreateNewEntityAndRole,
            onCreateNewRole
        },
        localize
    );

    return (
        <ContextualMenuContainer customProps={{ yMenuPosition }} style={{ transform: `translateX(${xMenuPosition}px)` }}>
            <DefaultButton
                componentRef={componentRef}
                disabled
                className={menuBtn}
                menuProps={{ items, styles: { container: contextualMenuContainer } }}
            />
        </ContextualMenuContainer>
    );
};
